// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the radius of the body a DSA is drawn on (DSA 1.9 §2,
 * `schema/dsa-v1.9-notes.md`), for vector sizes given in `km`:
 * degrees = km / R × 180 / π.
 *
 * R is the DSA's `bodyRadiusKm` when it gives one; else the body's mean
 * radius from the publisher's table (`schema/celestial-bodies-v1.json`,
 * copied below); else, for the Earth, the radius XR has always used. A
 * body missing from the table with no `bodyRadiusKm` has no radius: its
 * `km` sizes are never guessed (the vector layer is skipped).
 *
 * The body comes from `celestialBody` (1.9) or a `body:<name>` keyword
 * (older files), as offEarthBody.ts reads it.
 */

import { offEarthBody } from './offEarthBody'

/** The Earth's mean radius as XR's vector layers have always drawn it (111.195 km per degree). */
export const EARTH_RADIUS_KM = 6371.0088

/**
 * Mean radii in km (IAU WGCCRE 2015 / NASA planetary fact sheets), as the
 * publisher's `celestial-bodies-v1.json` lists them on 2026-10-14. A new
 * body joins the table only in a new version of that file; until then its
 * DSA gives `bodyRadiusKm`.
 */
export const BODY_RADII_KM: Readonly<Record<string, number>> = {
  sun: 695700.0,
  mercury: 2439.4,
  venus: 6051.8,
  earth: EARTH_RADIUS_KM,
  moon: 1737.4,
  mars: 3389.5,
  ceres: 469.7,
  jupiter: 69911.0,
  io: 1821.6,
  europa: 1560.8,
  ganymede: 2631.2,
  callisto: 2410.3,
  saturn: 58232.0,
  enceladus: 252.1,
  titan: 2574.7,
  uranus: 25362.0,
  neptune: 24622.0,
  triton: 1353.4,
  pluto: 1188.3,
  charon: 606.0,
}

/**
 * The radius in km of the body a parsed `.dsa` is drawn on, or null when
 * it names a body this table does not know and gives no `bodyRadiusKm`.
 */
export function bodyRadiusKmOf(dsa: { celestialBody?: unknown; bodyRadiusKm?: unknown; keywords?: unknown }): number | null {
  const given = dsa.bodyRadiusKm
  if (typeof given === 'number' && Number.isFinite(given) && given > 0) return given
  const body = offEarthBody({ celestialBody: dsa.celestialBody, keywords: Array.isArray(dsa.keywords) ? dsa.keywords : [] })
  if (!body) return EARTH_RADIUS_KM
  return BODY_RADII_KM[body] ?? null
}

/** Kilometres of surface per degree of arc on a sphere of `radiusKm`. */
export function kmPerDegree(radiusKm: number): number {
  return (radiusKm * Math.PI) / 180
}
